import { memo, useEffect, useMemo } from 'react';
import type { ProjectResultMatch } from '@frameio/graphql-client/generated';
import { plural } from '@frameio/i18n';
import type { ThemeSpaces } from '@frameio/vapor';
import {
  ProjectAvatar,
  Text,
  theme,
  useActiveWindowBreakpoint,
  useIsListBoxItemSelected,
} from '@frameio/vapor';
import { IconLock } from '@frameio/vapor-icons';
import { HighlightText } from '../../../../shared/components';
import { useTrackSearchResultsInspected } from '../../../../shared/contexts/SearchAnalyticsContext';
import type { TrackTabSelected } from '../../../../shared/contexts/SearchAnalyticsContext/types';
import { useQuickSearchProjectResultV2Fragment as useQuickSearchProjectResultFragment } from '../../../../shared/gql/generated/QuickSearchProjectResultV2';
import type { SectionId } from '../../../../shared/types';
import {
  getMatchTypeTrackingValues,
  getProjectStorage,
  getResultSourceFromSectionId,
} from '../../../../shared/utils';
import { ProjectContextMenuContent } from '../../QuickSearchContextMenu';
import {
  LIST_ITEM_MEDIA_HEIGHT,
  LIST_ITEM_MEDIA_HEIGHT_MOBILE,
  LIST_ITEM_MEDIA_WIDTH,
  LIST_ITEM_MEDIA_WIDTH_MOBILE,
} from '../constants';
import { getFieldMatch } from '../utils';
import TEXT from './generated/TEXT/ProjectListItem';
import { ListItemCard } from './ListItemCard';
import { ListItemDetailRow } from './ListItemDetailRow';
import { MediaContainer } from './MediaContainer';
import styles from './ProjectListItem.module.css';
import { useListItemContextMenu } from './useListItemContextMenu';

TEXT.DEFINE({
  /** Member count display with pluralization */
  memberCount: ({ count }: { count: number }) =>
    plural(count, `${count} members`, '1 member'),
  /** Label for inactive/archived project */
  inactive: 'Inactive Project',
  /** Label for restricted/private project */
  restricted: 'Restricted Project',
  /** Label for project type */
  project: 'Project',
  /** Accessibility label for project context menu button */
  contextMenuLabel: 'Project context menu',
});

type ProjectStatusProps = {
  isInactive: boolean;
  isPrivate: boolean;
};

type ProjectListItemProps = {
  disableHoverStyles?: boolean;
  isActive?: boolean;
  isAdvancedSearch?: boolean;
  isCompact?: boolean;
  itemPosition: number;
  paddingInlineEnd?: ThemeSpaces;
  projectId: string;
  sectionId?: SectionId;
  shouldUseBuiltInContextMenu?: boolean;
  tabSelected?: TrackTabSelected;
};

const ProjectStatusLabel = ({ isInactive, isPrivate }: ProjectStatusProps) => {
  if (!isInactive && !isPrivate) {
    return (
      <Text color="tertiary" variant="caption">
        {TEXT.project}
      </Text>
    );
  }

  return (
    <div className={styles.projectStatusLabel}>
      <Text color="tertiary" variant="caption">
        {isInactive ? TEXT.inactive : TEXT.restricted}
      </Text>
    </div>
  );
};

export const ProjectListItem = memo(
  ({
    disableHoverStyles = false,
    isActive: isActiveProp,
    isAdvancedSearch = false,
    isCompact = false,
    itemPosition,
    paddingInlineEnd,
    projectId,
    sectionId,
    shouldUseBuiltInContextMenu = true,
    tabSelected,
  }: ProjectListItemProps) => {
    const {
      data: { result: projectData, matches },
    } = useQuickSearchProjectResultFragment({
      from: { __typename: 'QuickSearchProjectResult', id: projectId },
    });
    const trackSearchResultsInspected = useTrackSearchResultsInspected();

    const webSource = isAdvancedSearch ? 'FullScreen' : 'Modal';
    const name = projectData?.name ?? '';
    const imageUrl = projectData?.imageUrl;
    const workspace = projectData?.workspace;
    const storage = projectData?.storage;
    const isPrivate = Boolean(projectData?.private);
    const isInactive = Boolean(projectData?.archivedAt);

    const isActiveFromListBox = useIsListBoxItemSelected();
    const isActive = isActiveProp ?? isActiveFromListBox;

    // Memoize match calculations
    const projectMatches = useMemo(
      () => (matches?.nodes ?? []) as ProjectResultMatch[],
      [matches?.nodes]
    );

    const nameHighlight = useMemo(
      () =>
        (getFieldMatch<ProjectResultMatch>(projectMatches, 'NameMatch')
          ?.terms as string[] | undefined) ?? undefined,
      [projectMatches]
    );

    const breakpoint = useActiveWindowBreakpoint();
    const isMobile = breakpoint === 'base';

    const mediaHeight =
      isMobile || isCompact
        ? LIST_ITEM_MEDIA_HEIGHT_MOBILE
        : LIST_ITEM_MEDIA_HEIGHT;
    const mediaWidth =
      isMobile || isCompact
        ? LIST_ITEM_MEDIA_WIDTH_MOBILE
        : LIST_ITEM_MEDIA_WIDTH;
    const mediaSize = isMobile || isCompact ? 48 : 64;

    const resultSource = getResultSourceFromSectionId(sectionId);

    useEffect(() => {
      if (isActive) {
        trackSearchResultsInspected?.({
          resultPosition: itemPosition,
          resultType: 'Project',
          objectName: name,
          resultSource,
          matchType: getMatchTypeTrackingValues(matches?.nodes ?? []),
          tabSelected,
          webSource,
        });
      }
    }, [
      isActive,
      itemPosition,
      tabSelected,
      trackSearchResultsInspected,
      name,
      resultSource,
      matches?.nodes,
      webSource,
    ]);

    const contextMenuContent = useMemo(
      () => (
        <ProjectContextMenuContent
          itemPosition={itemPosition}
          projectId={projectId}
          sectionId={sectionId}
        />
      ),
      [itemPosition, projectId, sectionId]
    );
    const { contextMenu: builtInContextMenu, onContextMenu } =
      useListItemContextMenu({
        buttonAccessibilityLabel: TEXT.contextMenuLabel,
        content: contextMenuContent,
        enabled: shouldUseBuiltInContextMenu,
      });

    return (
      <ListItemCard
        contextMenu={builtInContextMenu}
        detailComponent={
          <ListItemDetailRow
            leftSegment={
              <ProjectStatusLabel
                isInactive={isInactive}
                isPrivate={isPrivate}
              />
            }
            middleSegment={
              <Text color="tertiary" variant="caption">
                {getProjectStorage(storage)}
              </Text>
            }
            rightSegment={
              <Text
                color="tertiary"
                enableTooltip={true}
                truncate={true}
                variant="caption"
              >
                {workspace?.name}
              </Text>
            }
          />
        }
        disableHoverStyles={disableHoverStyles}
        imageComponent={
          <MediaContainer
            height={mediaHeight}
            isCompact={isMobile || isCompact}
            width={mediaWidth}
          >
            <div
              className={styles.projectAvatarWrapper}
              data-dimmed={isInactive}
            >
              <ProjectAvatar
                id={projectId}
                size={mediaSize}
                src={imageUrl ?? ''}
                title={name ?? ''}
              />
            </div>
            {(isPrivate || isInactive) && (
              <div aria-hidden="true" className={styles.restrictedBadge}>
                <IconLock
                  color={theme.colors.components.badge.icon}
                  size={16}
                />
              </div>
            )}
          </MediaContainer>
        }
        isActive={isActive}
        isCompact={isCompact}
        onContextMenu={onContextMenu}
        paddingInlineEnd={paddingInlineEnd}
        titleComponent={
          name && (
            <HighlightText
              highlights={nameHighlight}
              text={name ?? ''}
              truncate={true}
            />
          )
        }
      />
    );
  }
);

ProjectListItem.displayName = 'ProjectListItem';
